// Uses the current theme's formatter and observed variant:change form event.
import { formatMoney } from 'theme';

class IntranetSalePrice extends HTMLElement {
  connectedCallback() {
    this.variant = {
      price: Number(this.dataset.price),
      compare_at_price: Number(this.dataset.compare)
    };
    this.form = this.dataset.maxPrice === undefined ? document.forms[this.getAttribute('form')] : null;
    this.onVariant = event => {
      if (!event.detail?.variant) return;
      this.variant = event.detail.variant;
      this.renderPrice();
    };
    this.form?.addEventListener('variant:change', this.onVariant);
    this.refreshExpiry();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('variant:change', this.onVariant);
    clearTimeout(this.expiryTimer);
  }

  refreshExpiry() {
    clearTimeout(this.expiryTimer);
    this.renderPrice();
    const remaining = Number(this.dataset.endsAt) * 1000 - Date.now();
    if (remaining > 0) {
      this.expiryTimer = setTimeout(() => this.refreshExpiry(), Math.min(remaining + 50, 2147483647));
    }
  }

  renderPrice() {
    const base = Number(this.variant.price);
    const pay = Number(this.dataset.pay);
    const end = Number(this.dataset.endsAt);
    if (!Number.isFinite(base) || base < 0) return;
    const active = pay > 0 && pay < 100 && (end === 0 || end * 1000 > Date.now());
    const reduction = active ? Math.floor(base * (100 - pay) / 100) : 0;
    const current = base - reduction;
    const compare = reduction > 0 ? base : Number(this.variant.compare_at_price || 0);
    const max = this.dataset.maxPrice === undefined ? base : Number(this.dataset.maxPrice);
    const maxReduction = active ? Math.floor(max * (100 - pay) / 100) : 0;
    const onSale = compare > current || maxReduction > 0;
    const settings = window.themeVariables.settings;
    const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    const sale = this.querySelector('sale-price');
    const crossed = this.querySelector('compare-at-price');
    // Preserve the existing translated screen-reader label. Replace the complete
    // amount (money formats can contain several DOM nodes), never just lastChild.
    const replaceAmount = (element, amount, maximum) => {
      if (!element) return;
      const label = element.querySelector('.sr-only');
      let text = formatMoney(amount, format);
      if (this.dataset.maxPrice !== undefined && Number.isFinite(maximum) && maximum !== amount) {
        text = formatMoney(amount, settings.moneyFormat) + ' – ' + formatMoney(maximum, format);
      }
      if (element === sale && this.dataset.fromTemplate) {
        text = this.dataset.fromTemplate.replace('__INTRANET_PRICE__', () => text);
      }
      const nodes = document.createRange().createContextualFragment(text);
      element.replaceChildren(...(label ? [label] : []), nodes);
    };
    replaceAmount(sale, current, max - maxReduction);
    replaceAmount(crossed, compare, max);
    sale?.classList.toggle('text-on-sale', onSale);
    sale?.classList.toggle('text-subdued', !onSale);
    if (crossed) crossed.hidden = !onSale;
    const badge = this.querySelector('[data-intranet-discount]');
    if (badge) badge.hidden = reduction <= 0 && maxReduction <= 0;
  }
}

if (!customElements.get('intranet-sale-price')) {
  customElements.define('intranet-sale-price', IntranetSalePrice);
}
